/**
 * The last-resort update: drop every service-worker registration and EVERY
 * cache, then reload cold from the network.
 *
 * This is the one place in the app allowed to delete the Workbox precache
 * (docs/pwa-runbook.md invariant 10). It is correct here and only here because
 * it runs after an explicit update tap — the watchdog's "Update now", or the
 * banner's Update when the new worker never took control — whose whole meaning
 * is "fetch the latest build". `purgeUserCaches()` and
 * `cleanupStaleRuntimeCaches()` run unattended at boot and sign-out and must
 * never gain this behaviour. After the reload `initPwa()` re-registers the
 * worker and re-precaches.
 *
 * It is also built so it CANNOT brick the app. Deleting the precache and
 * unregistering the worker while the network is gone reloads the page into the
 * browser's offline error screen, with nothing left to serve the shell — and a
 * failed update check (`checkForUpdates()` → `'error'`) is exactly what a dead
 * connection or a captive portal looks like. So nothing is touched unless the
 * host that will replace those caches answers `/version.json` right now; a
 * `no-store` fetch of it cannot be satisfied by the worker (no route matches it,
 * invariant 8) or by the HTTP cache, so a 2xx really came over the network. An
 * HTML 200 (the SPA rewrite answering for a missing file) still proves the host
 * is up, which is all this needs to know.
 *
 * Imports only `./reload`, so both `register.ts` (a leaf) and `version-check.ts`
 * can use it without a cycle.
 */
import { reloadPage } from './reload';

/** Emitted at the site root by the build; never precached, never CDN-cached. */
export const VERSION_URL = '/version.json';

/** How long the reachability probe may take before the host counts as unreachable. */
const REACHABILITY_TIMEOUT_MS = 4_000;

/**
 * Ceiling on the unregister + cache purge. Each of those promises is unbounded
 * (a wedged worker can hold `unregister()` open), and the update wall is up the
 * whole time — so past this the page reloads regardless of how far it got.
 */
const CLEANUP_BUDGET_MS = 5_000;

export type HardReloadOutcome =
  /** The purge ran (or ran out of budget) and the reload was sent. */
  | 'reloading'
  /** Offline, or the host did not answer: NOTHING was touched. */
  | 'unreachable';

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Does the host that serves the app answer from the network right now?
 *
 * Any 2xx counts. Raced against a timer as well as aborted, so a fetch that
 * ignores its signal can never hold the caller past `timeoutMs`.
 */
export async function isOriginReachable(timeoutMs = REACHABILITY_TIMEOUT_MS): Promise<boolean> {
  if (typeof fetch === 'undefined') return false;
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => {
      controller?.abort();
      resolve(false);
    }, timeoutMs);
  });
  const probe = fetch(VERSION_URL, { cache: 'no-store', signal: controller?.signal })
    .then((res) => res.ok)
    .catch(() => false);
  try {
    return await Promise.race([probe, timedOut]);
  } finally {
    clearTimeout(timer);
  }
}

/** Unregister every registration, then delete every cache. Best effort throughout. */
async function purge(): Promise<void> {
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((reg) => reg.unregister().catch(() => false)));
    } catch {
      /* a failed unregister still leaves the cache purge and the reload */
    }
  }
  try {
    if (typeof caches !== 'undefined') {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name).catch(() => false)));
    }
  } catch {
    /* touching `caches` itself throws in a non-secure context */
  }
}

/**
 * Hard-reload onto the latest published build — or, when the host cannot be
 * reached, do nothing at all and say so.
 *
 * @param options.onCommit runs once, after the reachability check passed and
 *   before anything is deleted — the caller's "this is really happening" hook
 *   (record the watchdog attempt, flip the wall to "Restarting…").
 */
export async function hardReload(options: { onCommit?: () => void } = {}): Promise<HardReloadOutcome> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'unreachable';
  if (!(await isOriginReachable())) return 'unreachable';

  options.onCommit?.();
  await Promise.race([purge(), delay(CLEANUP_BUDGET_MS)]);
  reloadPage();
  return 'reloading';
}
